import React from 'react';
import { Application, Payment } from '../context/ApplicationContext';
import { v4 as uuidv4 } from 'uuid';
// Generate a random date within the last 30 days
const getRandomRecentDate = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const randomTime = thirtyDaysAgo.getTime() + Math.random() * (now.getTime() - thirtyDaysAgo.getTime());
  return new Date(randomTime).toISOString();
};
// Sample user IDs for demo
const userId1 = 'user-001';
const userId2 = 'user-002';
// Sample applications
export const sampleApplications: Application[] = [{
  id: uuidv4(),
  referenceNumber: 'TAX-2023-1001',
  userId: userId1,
  userName: 'John Doe',
  userEmail: 'john.doe@example.com',
  serviceId: '101',
  serviceName: 'Tax Clearance Certificate',
  mdaId: 'mda-001',
  mdaName: 'Edo State Internal Revenue Service',
  status: 'approved',
  submittedAt: getRandomRecentDate(),
  updatedAt: getRandomRecentDate(),
  formData: {
    fullName: 'John Doe',
    email: 'john.doe@example.com',
    phoneNumber: '08012345678',
    address: '123 Main Street, Benin City',
    taxId: 'TIN123456'
  },
  fee: 2500,
  isPaid: true,
  timeline: [{
    status: 'submitted',
    timestamp: getRandomRecentDate(),
    note: 'Application submitted successfully'
  }, {
    status: 'awaiting_payment',
    timestamp: getRandomRecentDate(),
    note: 'Application under review, awaiting payment'
  }, {
    status: 'payment_received',
    timestamp: getRandomRecentDate(),
    note: 'Payment of ₦2,500 received'
  }, {
    status: 'processing',
    timestamp: getRandomRecentDate(),
    note: 'Application processing in progress'
  }, {
    status: 'approved',
    timestamp: getRandomRecentDate(),
    note: 'Application approved. Certificate ready for download'
  }]
}, {
  id: uuidv4(),
  referenceNumber: 'BUS-2023-2002',
  userId: userId1,
  userName: 'John Doe',
  userEmail: 'john.doe@example.com',
  serviceId: '102',
  serviceName: 'Business Permit Renewal',
  mdaId: 'mda-002',
  mdaName: 'Ministry of Commerce and Industry',
  status: 'pending',
  submittedAt: getRandomRecentDate(),
  updatedAt: getRandomRecentDate(),
  formData: {
    businessName: 'Doe Enterprises',
    rcNumber: 'RC123456',
    address: '45 Commercial Avenue, Benin City',
    businessType: 'Retail'
  },
  fee: 5000,
  isPaid: false,
  timeline: [{
    status: 'submitted',
    timestamp: getRandomRecentDate(),
    note: 'Application submitted successfully'
  }, {
    status: 'awaiting_payment',
    timestamp: getRandomRecentDate(),
    note: 'Application under review, awaiting payment'
  }]
}, {
  id: uuidv4(),
  referenceNumber: 'LND-2023-3003',
  userId: userId1,
  userName: 'John Doe',
  userEmail: 'john.doe@example.com',
  serviceId: '401',
  serviceName: 'Certificate of Occupancy',
  mdaId: 'mda-003',
  mdaName: 'Ministry of Lands and Surveys',
  status: 'processing',
  submittedAt: getRandomRecentDate(),
  updatedAt: getRandomRecentDate(),
  formData: {
    propertyAddress: '78 GRA Extension, Benin City',
    plotSize: '1000 sqm',
    propertyType: 'Residential',
    surveyPlanNumber: 'SP/ED/2023/1234'
  },
  fee: 50000,
  isPaid: true,
  timeline: [{
    status: 'submitted',
    timestamp: getRandomRecentDate(),
    note: 'Application submitted successfully'
  }, {
    status: 'awaiting_payment',
    timestamp: getRandomRecentDate(),
    note: 'Application under review, awaiting payment'
  }, {
    status: 'payment_received',
    timestamp: getRandomRecentDate(),
    note: 'Payment of ₦50,000 received'
  }, {
    status: 'processing',
    timestamp: getRandomRecentDate(),
    note: 'Application under processing by Land Registry'
  }]
}, {
  id: uuidv4(),
  referenceNumber: 'HLT-2023-4004',
  userId: userId2,
  userName: 'Jane Smith',
  userEmail: 'jane.smith@example.com',
  serviceId: '201',
  serviceName: 'Health Facility Registration',
  mdaId: 'mda-004',
  mdaName: 'Ministry of Health',
  status: 'rejected',
  submittedAt: getRandomRecentDate(),
  updatedAt: getRandomRecentDate(),
  formData: {
    facilityName: 'Smith Medical Center',
    facilityType: 'Clinic',
    address: '15 Medical Road, Benin City',
    directorName: 'Dr. Jane Smith'
  },
  fee: 15000,
  isPaid: true,
  timeline: [{
    status: 'submitted',
    timestamp: getRandomRecentDate(),
    note: 'Application submitted successfully'
  }, {
    status: 'awaiting_payment',
    timestamp: getRandomRecentDate(),
    note: 'Application under review, awaiting payment'
  }, {
    status: 'payment_received',
    timestamp: getRandomRecentDate(),
    note: 'Payment of ₦15,000 received'
  }, {
    status: 'processing',
    timestamp: getRandomRecentDate(),
    note: 'Application under review by Health Ministry'
  }, {
    status: 'rejected',
    timestamp: getRandomRecentDate(),
    note: 'Application rejected. Reason: Incomplete documentation. Please resubmit with proper medical licenses.'
  }]
}];
// Sample payments
export const samplePayments: Payment[] = [{
  id: uuidv4(),
  referenceNumber: 'PMT-2023-1001',
  applicationId: sampleApplications[0].id,
  userId: userId1,
  userName: 'John Doe',
  amount: 2500,
  serviceName: 'Tax Clearance Certificate',
  mdaId: 'mda-001',
  mdaName: 'Edo State Internal Revenue Service',
  status: 'successful',
  date: getRandomRecentDate(),
  paymentMethod: 'Card Payment',
  transactionId: 'TRX123456'
}, {
  id: uuidv4(),
  referenceNumber: 'PMT-2023-3003',
  applicationId: sampleApplications[2].id,
  userId: userId1,
  userName: 'John Doe',
  amount: 50000,
  serviceName: 'Certificate of Occupancy',
  mdaId: 'mda-003',
  mdaName: 'Ministry of Lands and Surveys',
  status: 'successful',
  date: getRandomRecentDate(),
  paymentMethod: 'Bank Transfer',
  transactionId: 'TRX789012'
}, {
  id: uuidv4(),
  referenceNumber: 'PMT-2023-4004',
  applicationId: sampleApplications[3].id,
  userId: userId2,
  userName: 'Jane Smith',
  amount: 15000,
  serviceName: 'Health Facility Registration',
  mdaId: 'mda-004',
  mdaName: 'Ministry of Health',
  status: 'successful',
  date: getRandomRecentDate(),
  paymentMethod: 'Card Payment',
  transactionId: 'TRX345678'
}];